import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useWeather } from '../../context';
import { getUnitForOpenMeteoParameter } from '../../utils/units';

export function OpenMeteoLayer({ map }: { map: mapboxgl.Map }) {
  const { 
    weatherData, 
    modelSelect, 
    parameterSelect,
    resolution,
    triggerOperator,
    triggerValue,
    setWeatherData
  } = useWeather();
  
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const hoverPopupRef = useRef<mapboxgl.Popup | null>(null);
  const originalDataRef = useRef<any[]>([]);

  useEffect(() => {
    if (weatherData?.length > 1) {
      originalDataRef.current = weatherData;
    }
  }, [weatherData]);

  useEffect(() => {
    if (!map || modelSelect !== 'openmeteo' || !weatherData?.length) return;

    const cleanup = () => {
      if (map.getStyle()) {
        if (map.getLayer('openmeteo-grid')) {
          map.removeLayer('openmeteo-grid');
        }
        if (map.getSource('openmeteo-data')) {
          map.removeSource('openmeteo-data');
        }
      }
      if (popupRef.current) {
        popupRef.current.remove();
      }
      if (hoverPopupRef.current) {
        hoverPopupRef.current.remove();
      }
    };

    cleanup();

    try {
      const validData = weatherData.filter(point => 
        typeof point.lat === 'number' && 
        typeof point.lon === 'number' &&
        Array.isArray(point.values) &&
        point.values.length > 0
      );

      if (validData.length === 0) return;

      const features = validData.map(point => {
        const values = point.values.filter(v => v !== null);
        const value = parameterSelect.includes('_max')
          ? Math.max(...values)
          : parameterSelect.includes('_min')
            ? Math.min(...values)
            : values[values.length - 1];

        const halfRes = resolution / 2;

        return {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [point.lon - halfRes, point.lat - halfRes],
              [point.lon + halfRes, point.lat - halfRes],
              [point.lon + halfRes, point.lat + halfRes],
              [point.lon - halfRes, point.lat + halfRes],
              [point.lon - halfRes, point.lat - halfRes]
            ]]
          },
          properties: {
            value,
            lat: point.lat,
            lon: point.lon,
            times: point.times,
            values: point.values,
            parameter: parameterSelect,
            isTriggered: triggerOperator === '>' ? value > triggerValue : value < triggerValue
          }
        };
      });

      map.addSource('openmeteo-data', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features
        }
      });

      map.addLayer({
        id: 'openmeteo-grid',
        type: 'fill',
        source: 'openmeteo-data',
        paint: {
          'fill-color': [
            'case',
            ['get', 'isTriggered'],
            '#FF0000',
            [
              'interpolate',
              ['linear'],
              ['get', 'value'],
              -10, '#313695',
              0, '#4575b4',
              10, '#74add1',
              20, '#abd9e9',
              30, '#fee090',
              40, '#fdae61',
              50, '#f46d43',
              60, '#d73027'
            ]
          ],
          'fill-opacity': 0.7,
          'fill-outline-color': '#000000'
        }
      });

      popupRef.current = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        className: 'weather-data-popup-container'
      });

      hoverPopupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'weather-data-popup-container'
      });

      const handleCellClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
        if (!e.features?.length) return;
        
        const feature = e.features[0];
        const props = feature.properties;
        if (!props) return;

        // Transform data for the time series chart
        const pointData = {
          lat: props.lat,
          lon: props.lon,
          times: props.times,
          values: props.values,
          parameter: parameterSelect
        };

        // Update weather data with the selected point
        setWeatherData([{
          ...pointData,
          times: Array.isArray(props.times) ? props.times : JSON.parse(props.times),
          values: Array.isArray(props.values) ? props.values : JSON.parse(props.values)
        }]);

        if (popupRef.current) {
          const unit = getUnitForOpenMeteoParameter(parameterSelect);
          const html = `
            <div class="p-4">
              <h4 class="text-lg font-bold mb-2">Weather Data</h4>
              <p class="text-sm mb-2">Location: ${props.lat.toFixed(4)}°, ${props.lon.toFixed(4)}°</p>
              <p class="text-sm mb-2">Value: ${props.value.toFixed(2)}${unit}</p>
              <p class="text-xs text-[var(--secondary-text)]">Click anywhere outside to return to overview</p>
            </div>
          `;

          popupRef.current
            .setLngLat(e.lngLat)
            .setHTML(html)
            .addTo(map);
        }
      };

      const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['openmeteo-grid'] });
        if (features.length === 0 && originalDataRef.current.length > 0) {
          setWeatherData(originalDataRef.current);
          if (popupRef.current) {
            popupRef.current.remove();
          }
        }
      };

      const handleHover = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
        if (!e.features?.length) return;
        
        const feature = e.features[0];
        const props = feature.properties;
        
        map.getCanvas().style.cursor = 'pointer';

        if (hoverPopupRef.current && !popupRef.current?.isOpen()) {
          const unit = getUnitForOpenMeteoParameter(parameterSelect);
          const value = props.value?.toFixed(2) ?? 'N/A';
          
          hoverPopupRef.current
            .setLngLat(e.lngLat)
            .setHTML(`
              <div class="p-2">
                <p class="text-sm">${value}${unit}</p>
              </div>
            `)
            .addTo(map);
        }
      };

      map.on('click', 'openmeteo-grid', handleCellClick);
      map.on('click', handleMapClick);
      map.on('mousemove', 'openmeteo-grid', handleHover);
      map.on('mouseleave', 'openmeteo-grid', () => {
        map.getCanvas().style.cursor = '';
        if (hoverPopupRef.current && !popupRef.current?.isOpen()) {
          hoverPopupRef.current.remove();
        }
      });

      return cleanup;
    } catch (error) {
      console.error('Error rendering OpenMeteo layer:', error);
      cleanup();
    }
  }, [map, modelSelect, parameterSelect, weatherData, resolution, triggerOperator, triggerValue, setWeatherData]);

  return null;
}